import React, { useEffect, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import {
  ICheckboxField,
  ICheckboxGroup,
  IField,
  IFieldType,
  IFormSchema,
  IInputField,
  ISelectField,
} from "types";
import { i18nService } from "services";
import { useOnPersistedPageShow } from "utils";

import {
  CheckboxField,
  CheckboxGroup,
  InputField,
  SelectField,
} from "./fields";
import { FormText, SubmitButton } from "./components";

interface FormBuilderProps extends IFormSchema {
  submitLabel?: string;
  onFormSubmit?: SubmitHandler<any>;
  isSubmitted?: boolean;
  isSubmitting?: boolean;
  resetLabel?: string;
  handleReset?: () => void;
}
export const FormBuilder: React.FC<
  FormBuilderProps & React.FormHTMLAttributes<HTMLFormElement>
> = ({
  fields,
  submitLabel,
  onFormSubmit,
  isSubmitted,
  isSubmitting,
  disableAfterSubmit,
  resetLabel,
  handleReset,
  defaultValues,
  layout = "block",
  submitMode = "default",
  resetDefaultValues = true,
  ...rest // not defined form html attributes
}) => {
  const {
    reset,
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm({
    defaultValues,
  });

  const formIsDisabled = disableAfterSubmit && isSubmitted;

  const renderStaticField = (field: IField) => {
    const { type, name, ...rest } = field;
    switch (type) {
      case IFieldType.staticText: {
        return <FormText key={name} {...rest} />;
      }
    }
  };

  const renderField = (field: IField) => {
    if (field.type === IFieldType.staticText) {
      return renderStaticField(field);
    }

    const { name, type, value, validation = {}, ...rest } = field;

    const props = {
      required: !!validation?.required,
      error: errors[name],
      key: name,
      disabled: formIsDisabled,
      ...rest,
    };

    switch (type) {
      case IFieldType.text:
      case IFieldType.email:
      case IFieldType.password:
      case IFieldType.search: {
        const { value: inputValue } = field as IInputField;

        return (
          <InputField
            type={type}
            {...register(name, validation)}
            {...props}
            value={inputValue}
          />
        );
      }

      case IFieldType.checkbox: {
        const { value: inputValue } = field as ICheckboxField;

        return (
          <CheckboxField
            type={type}
            id={name}
            {...register(name, validation)}
            {...props}
            value={inputValue}
          />
        );
      }

      case IFieldType.checkboxGroup: {
        const { options } = field as ICheckboxGroup;

        if (!options.length) return null;

        return (
          <CheckboxGroup
            name={name}
            control={control}
            validation={validation}
            options={options}
            type={type}
            {...props}
            value={watch(name) || []}
          />
        );
      }

      case IFieldType.select: {
        const { options } = field as ISelectField;

        if (!options.length) return null;
        if (value && typeof value !== "string") return null;

        return (
          <SelectField
            type={type}
            value={value}
            {...register(name, validation)}
            {...props}
            options={options}
          />
        );
      }
    }
  };

  const onReset = () => {
    handleReset && handleReset();
    reset();
  };

  // reset default values if changed after init render
  useEffect(() => {
    if (defaultValues && resetDefaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset, resetDefaultValues]);

  const isSubmitDisabled = useMemo(() => {
    if (submitMode === "onDirty") return !isDirty;
    if (submitMode === "onValid") return !isValid;

    return isSubmitted || isSubmitting;
  }, [submitMode, isSubmitted, isSubmitting, isValid, isDirty]);

  // bfcache fix
  useOnPersistedPageShow(() => reset());

  return (
    <form
      className={`form is-${layout}`}
      onSubmit={onFormSubmit && handleSubmit(onFormSubmit)}
      {...rest} // not defined form html attributes
    >
      {fields.map((field) => renderField(field))}
      <div className="form__actions">
        <div className="button-container">
          {submitLabel && (
            <SubmitButton
              text={submitLabel}
              disabled={isSubmitDisabled}
              isSubmitting={isSubmitting}
            />
          )}
          {formIsDisabled && (
            <button
              className="link is-bold is-small"
              type="button"
              onClick={onReset}
            >
              <span className="link__inner">
                <span className="link__text">
                  {resetLabel || i18nService.copy["general.reset"]}
                </span>
              </span>
            </button>
          )}
        </div>
      </div>
    </form>
  );
};
